import React, { createContext, Fragment, useContext, useState } from "react";

import { faChevronDown } from "@fortawesome/pro-solid-svg-icons";
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from "@headlessui/react";
import { AnimatePresence, m } from "framer-motion";
import { twJoin } from "tailwind-merge";

import { Icon } from "src/base-components/Icon";
import { SkeletonPlaceholder } from "src/base-components/SkeletonPlaceholder";

type ControlledProps = {
  value: string[];
  onChange: (value: string[]) => void;
  defaultValue?: never;
};

type UncontrolledProps = {
  defaultValue?: string[];
  onChange?: never;
  value?: never;
};

type AccordionProps = (ControlledProps | UncontrolledProps) & {
  children: React.ReactNode;
  dataLoc?: string;
  loadingSkeleton?: React.ReactNode;
};

type AccordionContextValue = {
  isItemOpen: (value: string) => boolean;
  handleToggle: (value: string) => void;
  loadingSkeleton: React.ReactNode;
  dataLoc: string;
};

const AccordionContext = createContext<AccordionContextValue | null>(null);

const useAccordionContext = () => {
  const ctx = useContext(AccordionContext);
  if (!ctx) {
    throw new Error(
      "Accordion.Item must be used within an Accordion component",
    );
  }
  return ctx;
};

type AccordionItemProps = {
  value: string;
  title: string;
  description?: React.ReactNode;
  rightSlot?: React.ReactNode;
  dataLoc?: string;
  loadingSkeleton?: React.ReactNode;
  isLoading?: boolean;
  forceMount?: boolean;
} & (
  | {
      children: React.ReactNode;
      disabled?: boolean;
      readOnly?: boolean;
    }
  | {
      children?: never;
      disabled?: never;
      readOnly?: never;
    }
);

type AccordionItemContentProps = AccordionItemProps & {
  open: boolean;
  onToggle: (value: string) => void;
};

const AccordionItemContent: React.FC<AccordionItemContentProps> = ({
  value,
  onToggle,
  title,
  description,
  children,
  disabled,
  readOnly,
  rightSlot,
  dataLoc = "accordion-item",
  open,
  loadingSkeleton,
  isLoading,
  forceMount,
}) => {
  const interactionDisabled = disabled || readOnly;

  return (
    <>
      <div className="flex items-start gap-x-2 p-4">
        <DisclosureButton
          className={twJoin(
            "group flex w-full items-start gap-x-2 text-left",
            interactionDisabled && "cursor-default",
          )}
          data-loc={`${dataLoc}-trigger`}
          disabled={interactionDisabled}
          onClick={() => onToggle(value)}
        >
          <div
            className={twJoin(
              "flex h-5 w-5 shrink-0 items-center justify-center transition-transform duration-200",
              open ? "rotate-0" : "-rotate-90",
              disabled ? "text-gray-300" : "text-gray-800",
            )}
          >
            <Icon icon={faChevronDown} size="xs" />
          </div>
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <span className="font-inter-semibold-13px text-gray-800">
              {title}
            </span>
            {description && (
              <span className="font-inter-medium-12px text-gray-500">
                {description}
              </span>
            )}
          </div>
        </DisclosureButton>
        {rightSlot && <div className="mt-0.5 flex shrink-0">{rightSlot}</div>}
      </div>
      <AnimatePresence initial={false}>
        {(forceMount || open) && (
          <DisclosurePanel as={Fragment} static>
            <m.div
              key="panel"
              animate={
                open
                  ? { height: "auto", opacity: 1 }
                  : { height: 0, opacity: 0 }
              }
              className="overflow-hidden"
              exit={{ height: 0, opacity: 0 }}
              initial={{ height: 0, opacity: 0 }}
              role="region"
              transition={{ duration: 0.2, ease: "easeOut" }}
            >
              <div className="pr-6 pb-6 pl-11 font-inter-normal-13px text-gray-800">
                {isLoading ? loadingSkeleton : children}
              </div>
            </m.div>
          </DisclosurePanel>
        )}
      </AnimatePresence>
    </>
  );
};

type AccordionItemStaticProps = Pick<
  AccordionItemProps,
  "title" | "description" | "rightSlot" | "dataLoc"
>;

const AccordionItemStatic: React.FC<AccordionItemStaticProps> = ({
  title,
  description,
  rightSlot,
  dataLoc = "accordion-item",
}) => {
  return (
    <div
      className={twJoin("flex items-start gap-x-2 p-4")}
      data-loc={`${dataLoc}-trigger`}
    >
      <div className="flex w-full items-start gap-x-2 text-left">
        <div className="h-5 w-5 shrink-0" />
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="font-inter-semibold-13px text-gray-800">
            {title}
          </span>
          {description && (
            <span className="font-inter-medium-12px text-gray-500">
              {description}
            </span>
          )}
        </div>
      </div>
      {rightSlot && <div className="shrink-0">{rightSlot}</div>}
    </div>
  );
};

const AccordionItem: React.FC<AccordionItemProps> = ({
  value,
  title,
  description,
  children,
  disabled,
  readOnly,
  rightSlot,
  dataLoc,
  loadingSkeleton: itemLoadingSkeleton,
  isLoading,
  forceMount,
}) => {
  const ctx = useAccordionContext();
  const itemDataLoc = dataLoc ?? `${ctx.dataLoc}-item`;
  const skeleton = itemLoadingSkeleton ?? ctx.loadingSkeleton;

  const rawOpen = ctx.isItemOpen(value);
  const effectiveOpen = disabled ? false : rawOpen;

  if (children != null) {
    return (
      <Disclosure as="div" data-loc={itemDataLoc} defaultOpen={effectiveOpen}>
        <AccordionItemContent
          dataLoc={dataLoc}
          description={description}
          disabled={disabled}
          forceMount={forceMount}
          isLoading={isLoading}
          loadingSkeleton={skeleton}
          open={effectiveOpen}
          readOnly={readOnly}
          rightSlot={rightSlot}
          title={title}
          value={value}
          onToggle={ctx.handleToggle}
        >
          {children}
        </AccordionItemContent>
      </Disclosure>
    );
  }

  return (
    <div data-loc={itemDataLoc}>
      <AccordionItemStatic
        dataLoc={dataLoc}
        description={description}
        rightSlot={rightSlot}
        title={title}
      />
    </div>
  );
};

const AccordionSkeleton: React.FC = () => {
  return (
    <div className="flex flex-col gap-y-3">
      <SkeletonPlaceholder height="h-6" width="w-full" />
      <SkeletonPlaceholder height="h-6" width="w-3/4" />
      <SkeletonPlaceholder height="h-6" width="w-5/6" />
    </div>
  );
};
/**
 * Figma: https://www.figma.com/design/BO9DWO8PKNGNSI3HhpSobA/Design-System-v2?node-id=7904-7970
 */
export const Accordion: React.FC<AccordionProps> & {
  Item: typeof AccordionItem;
} = ({
  children,
  dataLoc = "accordion",
  loadingSkeleton = <AccordionSkeleton />,
  ...props
}) => {
  const { defaultValue, onChange, value } = props;
  const isControlled = value !== undefined;

  const [internalOpen, setInternalOpen] = useState<string[]>(
    () => defaultValue ?? [],
  );

  const isItemOpen = (itemValue: string): boolean => {
    if (isControlled) {
      return value.includes(itemValue);
    }
    return internalOpen.includes(itemValue);
  };

  const handleToggle = (val: string) => {
    if (isControlled) {
      const newValue = value.includes(val)
        ? value.filter((v) => v !== val)
        : [...value, val];
      onChange(newValue);
    } else {
      setInternalOpen((prev) =>
        prev.includes(val) ? prev.filter((v) => v !== val) : [...prev, val],
      );
    }
  };

  const contextValue: AccordionContextValue = {
    isItemOpen,
    handleToggle,
    loadingSkeleton,
    dataLoc,
  };

  return (
    <div
      className="divide-y divide-gray-200 rounded-lg border border-gray-200"
      data-loc={dataLoc}
    >
      <AccordionContext.Provider value={contextValue}>
        {children}
      </AccordionContext.Provider>
    </div>
  );
};

Accordion.Item = AccordionItem;

/**
 * Hook that provides controlled state for single-mode accordion behavior
 * (at most one item open at a time).
 *
 * Usage:
 *   const { value, onChange } = useAccordionSingleMode("item-1");
 *   <Accordion value={value} onChange={onChange}>...</Accordion>
 */
export const useAccordionSingleMode = (defaultValue?: string) => {
  const [value, setValue] = useState<string[]>(
    defaultValue !== undefined ? [defaultValue] : [],
  );

  const onChange = (next: string[]) => {
    const opened = next.filter((v) => !value.includes(v));
    setValue(opened.length > 0 ? [opened[0]] : []);
  };

  return { value, onChange } as const;
};
